<template>
  <WdsModal :show="show" :title="$t('Edit Project')" @cancel="close">
    <div>
      <div class="tw-mb-6">
        <WdsInput
          v-model="projectName"
          :label="$t('Name')"
          :placeholder="$t('Give your project a name')"
          :disabled="loading"
        />
      </div>
      <div class="tw-mb-6">
        <WdsInputArea
          v-model="projectDescription"
          :label="$t('Description')"
          :placeholder="$t('Describe your project details')"
          :disabled="loading"
        />
      </div>
      <div class="tw-mb-6">
        <WdsInputCurrency
          v-model="projectBudget"
          :config="rateConfig"
          :label="$t('Project budget')"
          :disabled="loading"
        />
      </div>
      <div class="tw-mb-6">
        <label>{{ $t('Owner(s)') }}</label>
        <br />
        <WdsSelect
          v-model="owners"
          multi-select
          can-deselect
          searchable
          :list="companyUsers"
          :placeholder="$t('Select owners')"
          @load-more="loadMoreCompanyUsers"
        />
      </div>
    </div>
    <template #footer>
      <WdsStack justify-space-between>
        <WdsButton tertiary @click="close">{{ $t('Cancel') }}</WdsButton>
        <WdsStack gap-sm align-center>
          <WdsSpinner v-if="loading" indeterminate />
          <WdsButton primary :disabled="loading || inputUnchanged" @click="submit">
            {{ $t('Save changes') }}
          </WdsButton>
        </WdsStack>
      </WdsStack>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import {
  WdsInput,
  WdsInputCurrency,
  WdsInputArea,
  WdsButton,
  WdsSpinner,
  WdsStack,
  WdsModal,
  WdsSelect,
} from '@worksome/wds-components'
import gql from 'graphql-tag'
import { computed, ref, onBeforeMount, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { publicApi } from '@/app-bootstrapping/SetUpApollo'
import { NetworkError } from '@/utils/notification'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

const props = defineProps({
  project: {
    required: true,
    type: Object,
  },
})

type OwnerOption = {
  value: String
  label: String
}

type PaginationInfo = {
  hasMorePages: Boolean
  currentPage: Number
}

const owners = ref<OwnerOption[]>(
  props.project.owners.map((owner) => ({ value: owner.id, label: owner.name })),
)
const ownerIds = computed(() => owners.value.map((owner) => owner.value))

const companyUsers = ref<OwnerOption[]>([])
const companyUsersPaginationInfo = ref<PaginationInfo>({
  hasMorePages: false,
  currentPage: 1,
})
const companyUsersPage = ref(1)

onBeforeMount(() => {
  companyUsers.value = companyUsers.value.concat([
    {
      value: window?.currentAccountOwner?.globalId,
      label: window?.currentAccountOwner?.name,
    },
  ])
})

const { result, onResult } = useQuery(
  gql`
    query accounts($page: Int!) {
      accounts {
        id
        ... on Company {
          teamMembers(page: $page) {
            data {
              id
              name
            }
            paginatorInfo {
              currentPage
              hasMorePages
            }
          }
        }
      }
    }
  `,
  {
    page: companyUsersPage,
  },
  {
    clientId: 'publicApi',
  },
)

const handleResult = (resultValue) => {
  const company = resultValue?.accounts
    ?.filter(
      (account) =>
        account.__typename === 'Company' && account.id === window?.currentAccount.globalId,
    )
    .pop()

  if (company) {
    const data = company.teamMembers.data.map((teamMember) => ({
      value: teamMember.id,
      label: teamMember.name,
    }))
    companyUsers.value = companyUsers.value.concat(data)
    companyUsersPaginationInfo.value = company.teamMembers.paginatorInfo
  }
}

// because this same query is called in a different component
// within the parent onResult is not called again and
// result therefore already contains a value
onResult((resultValue) => {
  handleResult(resultValue.data)
})

watch(
  () => result,
  (resultValue) => {
    handleResult(resultValue.value)
  },
  { immediate: true },
)

const loadMoreCompanyUsers = () => {
  if (companyUsersPaginationInfo.value?.hasMorePages) {
    companyUsersPage.value = companyUsersPaginationInfo.value.currentPage.valueOf() + 1
  }
}

const emit = defineEmits(['close', 'submitted'])

const currency = window?.currentAccount?.currency

const projectName = ref(props.project.name)
const projectDescription = ref(props.project.description)
const projectBudget = ref(`${props.project.internalBudget}`)
const loading = ref(false)
const show = ref(true)

const inputUnchanged = computed(
  () =>
    projectName.value === props.project.name &&
    projectDescription.value === props.project.description &&
    projectBudget.value === props.project.budget &&
    owners.value === props.project.owners.map((owner) => ({ value: owner.id, label: owner.name })),
)

const rateConfig = computed(() => ({
  currencySymbol: currency ?? '',
  thousandsSeparator: ',',
  decimalSeparator: '.',
  showFractions: true,
}))

function close() {
  show.value = false
  setTimeout(() => emit('close'), 150)
}

async function submit() {
  // Submitting
  loading.value = true
  try {
    const res = await publicApi.mutate({
      mutation: gql`
        mutation UpdateProject($input: UpdateProjectInput!) {
          updateProject(input: $input) {
            id
            name
            description
            internalBudget
            owners {
              id
              name
            }
          }
        }
      `,
      variables: {
        input: {
          id: props.project.id,
          name: projectName.value,
          description: projectDescription.value,
          internalBudget: parseFloat(projectBudget.value),
          owners: ownerIds.value,
        },
      },
    })

    await publicApi.writeQuery({
      query: gql`
        query Project($id: Int!) {
          project(id: $id) {
            id
            name
            description
            internalBudget
            owners {
              id
              name
            }
          }
        }
      `,
      data: {
        // Contains the data to write
        project: {
          ...props.project,
          ...res.data.updateProject,
        },
      },
      variables: {
        id: props.project.id,
      },
    })
    emit('submitted')
    close()
  } catch (exp) {
    const ex = exp as any
    new NetworkError({
      msg: $t('Error occurred. The action was not completed'),
      ex,
    }).dispatch()
    loading.value = false
  }
}
</script>
